/**
 * Quote Expiration Service - Handles quote expiration workflows
 */

import api from "../../../shared/api.jsx";
import { logError } from "../../../shared/utils/logger.js";

/**
 * Get expiration statistics
 * @returns {Promise<Object>} Expiration statistics
 */
export const getExpirationStats = async () => {
  try {
    const response = await api.get("/quotes/expiration/stats");
    return response;
  } catch (error) {
    logError("Error fetching expiration stats", {
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch expiration stats",
    };
  }
};

/**
 * Get quotes approaching expiration
 * @param {number} daysThreshold - Days threshold for approaching expiration
 * @returns {Promise<Object>} Quotes approaching expiration
 */
export const getApproachingQuotes = async (daysThreshold = 7) => {
  try {
    const response = await api.get(
      `/quotes/expiration/approaching?daysThreshold=${daysThreshold}`
    );
    return response;
  } catch (error) {
    logError("Error fetching approaching quotes", {
      daysThreshold,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch approaching quotes",
    };
  }
};

/**
 * Get worker status
 * @returns {Promise<Object>} Worker status information
 */
export const getWorkerStatus = async () => {
  try {
    const response = await api.get("/quotes/expiration/worker/status");
    return response;
  } catch (error) {
    logError("Error fetching worker status", {
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch worker status",
    };
  }
};

/**
 * Trigger expiration check
 * @returns {Promise<Object>} Expiration check result
 */
export const triggerExpirationCheck = async () => {
  try {
    const response = await api.post("/quotes/expiration/check");
    return response;
  } catch (error) {
    logError("Error triggering expiration check", {
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to trigger expiration check",
    };
  }
};

/**
 * Send expiration warnings
 * @param {Object} warningData - Warning data including days threshold
 * @returns {Promise<Object>} Warning result
 */
export const sendExpirationWarnings = async (warningData) => {
  try {
    const response = await api.post("/quotes/expiration/warnings", warningData);
    return response;
  } catch (error) {
    logError("Error sending expiration warnings", {
      warningData,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to send expiration warnings",
    };
  }
};

/**
 * Manually expire a quote
 * @param {string} quoteId - Quote ID to expire
 * @param {Object} expirationData - Expiration data including reason
 * @returns {Promise<Object>} Expiration result
 */
export const manuallyExpireQuote = async (quoteId, expirationData) => {
  try {
    const response = await api.post(
      `/quotes/expiration/manual/${quoteId}`,
      expirationData
    );
    return response;
  } catch (error) {
    logError("Error manually expiring quote", {
      quoteId,
      expirationData,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to manually expire quote",
    };
  }
};
